"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Dispatch, SetStateAction } from "react";
import type { components } from "@zapier/api-clients/apis/usa/types";

import type {
  TemplateBookmarkContextValue,
  TemplateBookmarkProviderProps,
} from "./TemplateBookmarkContext.types";
import type { TemplateBookmark } from "../types/bookmark";
import { getJson, postJson, deleteJson } from "../utils/fetchJson";

const TemplateBookmarkContext = createContext<
  TemplateBookmarkContextValue | undefined
>(undefined);

type LoadBookmarksParams = {
  apiHostName: string;
  isLoggedIn?: boolean;
  onSuccess?: TemplateBookmarkProviderProps["onSuccess"];
  onError?: TemplateBookmarkProviderProps["onError"];
  setBookmarks: Dispatch<SetStateAction<TemplateBookmark[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

type UpstreamBookmark = components["schemas"]["UserTemplate"];
type UpstreamBookmarkList = components["schemas"]["PaginatedUserTemplateList"];

const upstreamToBookmark = (upstream: UpstreamBookmark): TemplateBookmark => ({
  templateId: upstream.template_id,
  savedAt: upstream.created_at,
});

async function fetchBookmarksFromApi(
  apiHostName: string,
): Promise<TemplateBookmark[]> {
  const allBookmarks: TemplateBookmark[] = [];
  let offset = 0;
  const limit = 100;
  let hasMore = true;

  while (hasMore) {
    const baseUrl = `${apiHostName}/api/templates/bookmarks`;
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ordering: "-created_at",
    });
    const url = `${baseUrl}?${params.toString()}`;

    const data = await getJson<UpstreamBookmarkList>(url);

    allBookmarks.push(...(data.results ?? []).map(upstreamToBookmark));

    hasMore = Boolean(data.next);
    offset += limit;
  }

  return allBookmarks;
}

async function loadBookmarks({
  apiHostName,
  isLoggedIn,
  onSuccess,
  onError,
  setBookmarks,
  setIsLoading,
}: LoadBookmarksParams): Promise<void> {
  // Skip fetching if user is not logged in
  if (!isLoggedIn) {
    setBookmarks([]);
    setIsLoading(false);
    return;
  }

  setIsLoading(true);

  try {
    const fetchedBookmarks = await fetchBookmarksFromApi(apiHostName);
    setBookmarks(fetchedBookmarks);
    onSuccess?.("fetch");
  } catch (error) {
    const errorObj =
      error instanceof Error ? error : new Error("Failed to fetch bookmarks");
    onError?.(errorObj, "fetch");
    throw errorObj;
  } finally {
    setIsLoading(false);
  }
}

export function TemplateBookmarkProvider({
  children,
  isLoggedIn,
  apiHostName = "",
  onSuccess,
  onError,
}: TemplateBookmarkProviderProps) {
  const [bookmarks, setBookmarks] = useState<TemplateBookmark[]>([]);
  const [isLoading, setIsLoading] = useState(isLoggedIn !== false);
  const [changingTemplateId, setChangingTemplateId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    loadBookmarks({
      apiHostName,
      isLoggedIn,
      onSuccess,
      onError,
      setBookmarks,
      setIsLoading,
    }).catch(() => {
      // error already handled in loadBookmarks
    });
  }, [apiHostName, isLoggedIn, onError, onSuccess]);

  const isBookmarked = useCallback(
    (templateId: string) =>
      bookmarks.some((bookmark) => bookmark.templateId === templateId),
    [bookmarks],
  );

  const createUserTemplateBookmark = useCallback(
    async (templateId: string) => {
      // Optimistic update: stamp `savedAt` with the current time as a
      // placeholder so UI doesn't flash. The POST response then replaces
      // this entry with the server-authoritative record (see below) — any
      // future curated field on `TemplateBookmark` benefits from that
      // replacement without changes to this path.
      const optimisticBookmark: TemplateBookmark = {
        templateId,
        savedAt: new Date().toISOString(),
      };
      setBookmarks((prev) =>
        prev.some((bookmark) => bookmark.templateId === templateId)
          ? prev
          : [optimisticBookmark, ...prev],
      );
      setChangingTemplateId(templateId);

      try {
        const upstream = await postJson<UpstreamBookmark>(
          `${apiHostName}/api/templates/bookmarks`,
          { template_id: templateId },
        );
        const serverBookmark = upstreamToBookmark(upstream);
        setBookmarks((prev) =>
          prev.map((bookmark) =>
            bookmark.templateId === templateId ? serverBookmark : bookmark,
          ),
        );
        onSuccess?.("create", templateId);
      } catch (error) {
        setBookmarks((prev) =>
          prev.filter((bookmark) => bookmark.templateId !== templateId),
        );

        const errorObj =
          error instanceof Error
            ? error
            : new Error("Failed to create bookmark");

        onError?.(errorObj, "create", templateId);
        throw errorObj;
      } finally {
        setChangingTemplateId(null);
      }
    },
    [apiHostName, onError, onSuccess],
  );

  const deleteUserTemplateBookmark = useCallback(
    async (templateId: string) => {
      setChangingTemplateId(templateId);

      try {
        await deleteJson(
          `${apiHostName}/api/templates/bookmarks/${templateId}`,
        );
        setBookmarks((prev) =>
          prev.filter((bookmark) => bookmark.templateId !== templateId),
        );
        onSuccess?.("delete", templateId);
      } catch (error) {
        const errorObj =
          error instanceof Error
            ? error
            : new Error("Failed to delete bookmark");

        onError?.(errorObj, "delete", templateId);
        throw errorObj;
      } finally {
        setChangingTemplateId(null);
      }
    },
    [apiHostName, onError, onSuccess],
  );

  const refreshBookmarks = useCallback(
    () =>
      loadBookmarks({
        apiHostName,
        isLoggedIn,
        onSuccess,
        onError,
        setBookmarks,
        setIsLoading,
      }),
    [apiHostName, isLoggedIn, onError, onSuccess],
  );

  const contextValue = useMemo(
    () => ({
      bookmarks,
      isBookmarked,
      createUserTemplateBookmark,
      deleteUserTemplateBookmark,
      refreshBookmarks,
      isLoadingBookmarks: isLoading,
      templateBookmarkChanging: changingTemplateId,
    }),
    [
      bookmarks,
      isBookmarked,
      createUserTemplateBookmark,
      deleteUserTemplateBookmark,
      refreshBookmarks,
      isLoading,
      changingTemplateId,
    ],
  );

  return (
    <TemplateBookmarkContext.Provider value={contextValue}>
      {children}
    </TemplateBookmarkContext.Provider>
  );
}

/**
 * Hook to access template bookmark context.
 * Must be used within a TemplateBookmarkProvider.
 */
export function useTemplateBookmarkContext(): TemplateBookmarkContextValue | null {
  const context = useContext(TemplateBookmarkContext);
  return context || null;
}
